import {
  selectPodcast,
  setPodcasts,
  setPodcastsFailed,
  setPodcastsLoading,
  touchPodcasts,
} from "../../redux/reducers/podcastReducer";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef } from "react";

import { getAllPodcasts } from "../../services/podcastService";
import { isCacheFresh } from "./cachePolicy";
import { selectUser } from "../../redux/reducers/userReducer";

export const usePodcast = () => {
  const dispatch = useDispatch();
  const podcast = useSelector(selectPodcast);
  const user = useSelector(selectUser);

  const email = user?.data?.user?.user_id || "";
  const revalidating = useRef(false);

  useEffect(() => {
    const cacheValid =
      podcast.podcasts.length > 0 && podcast.cachedForEmail === email;

    const run = async () => {
      if (!cacheValid) {
        // Cold start (or the user changed): the page renders a skeleton view
        // while status === 'loading' — no global spinner.
        try {
          dispatch(setPodcastsLoading());
          const data = await getAllPodcasts({ email });
          dispatch(setPodcasts({ podcasts: data.data, email }));
        } catch (error) {
          dispatch(setPodcastsFailed());
          console.log("Error fetching podcasts");
        }
        return;
      }

      // Warm cache: render instantly, revalidate silently in the background —
      // unless the cache is fresh, in which case skip the network entirely.
      if (isCacheFresh(podcast.lastFetched)) return;
      if (revalidating.current) return;
      revalidating.current = true;
      try {
        const data = await getAllPodcasts({ email });
        const fresh = data.data ?? [];
        const cached = podcast.podcasts;
        const unchanged =
          fresh.length === cached.length && fresh[0]?.id === cached[0]?.id;
        if (unchanged) {
          dispatch(touchPodcasts());
        } else {
          dispatch(setPodcasts({ podcasts: fresh, email }));
        }
      } catch (error) {
        // Background refresh failing is invisible; keep serving the cache.
      } finally {
        revalidating.current = false;
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return {
    podcasts: podcast.podcasts,
    // Skeleton state: only on a true cold start (nothing cached yet) —
    // warm-cache visits render instantly and background refreshes are silent.
    isLoading: podcast.status === "loading" && podcast.podcasts.length === 0,
  };
};
